/**
 * Ein Feld, in dem man tippt und aus Treffern wählt.
 *
 * Warum kein einfaches `select`: Bei 128 Orten ist ein Aufklappmenü eine
 * Scrollstrecke. Wer den richtigen Eintrag sucht, findet ihn schneller, indem
 * er drei Buchstaben tippt — und tippt dabei nichts Neues an, was eine neue
 * Schreibweise erzeugen würde.
 *
 * Warum kein aufwendiges schwebendes Menü: Die Trefferliste steht einfach
 * darunter und schiebt den Rest nach unten. Das ist unruhiger, aber es kann
 * nicht am falschen Fleck landen, nicht abgeschnitten werden und nicht hinter
 * einer Karte verschwinden — drei Fehlerquellen, die man sich mit einer
 * schwebenden Liste einkauft und die im Büro dann niemand erklären kann.
 *
 * Gesucht wird fehlertolerant (daten/textvergleich.ts): „hersch" muss „Werk
 * Herrschaftsholz" finden.
 *
 * ═══ NEU ANLEGEN, OHNE DIE SEITE ZU VERLASSEN ═══
 *
 * Wer eine Tour einträgt und dabei merkt, dass der Entladeort noch fehlt,
 * musste vorher nach „Orte", dort anlegen, zurück — und das halbfertige
 * Formular war weg. Zwei Wege haben das behoben: der Entwurf (siehe
 * `daten/entwurf.ts`) und dieser Knopf.
 *
 * Er steht bewusst UNTER den Treffern und nicht daneben. Die Reihenfolge ist
 * die Frage, die sich stellt: Erst „ist es einer von diesen?", dann „nein,
 * dann neu". Andersherum legt man an, was schon da ist — und aus 128 Orten
 * werden wieder 214 Schreibweisen.
 *
 * ═══ DAS ANLEGEN STEHT IMMER DA, AUCH BEI GLEICHEM NAMEN (HJ-042, HJ-014) ═══
 *
 * Früher verschwand der Knopf, sobald es den Namen wortgleich schon gab. Aber
 * mehrere Kunden heißen gleich und sitzen woanders — wer den zweiten Standort
 * von „Korn Recycling" brauchte, kam hier nicht weiter und tippte die Anschrift
 * in den Namen. Die Datenbank kennt keine Eindeutigkeit des Namens, die Sperre
 * saß nur hier. Gegen Dubletten hilft jetzt, was dabei wirklich hilft: Die
 * Treffer zeigen ihre Anschrift, davor steht „Ähnliche Einträge gefunden", und
 * wer anlegt, bestätigt mit „Ist neu – anlegen".
 */

import { useMemo, useState, type ReactNode } from 'react';
import { trefferWaehlen } from '../daten/trefferwahl';

/**
 * Ein Eintrag der Auswahl.
 *
 * `unter` ist die zweite, kleine Zeile — die Anschrift. Sie ist kein Schmuck: „Korn Recycling" gibt es in Albstadt UND in Ulm,
 * und mit dem Namen allein sieht niemand, welchen Standort er gerade wählt.
 */
type Eintrag = { id: string; name: string; unter?: string | null };

export function Auswahlsuche(eigenschaften: {
  marke: string;
  hinweis?: string;
  /** Die aktuell gewählte Kennung, oder null. */
  wert: string | null;
  setzen: (id: string | null) => void;
  auswahl: Eintrag[];
  platzhalter?: string;
  disabled?: boolean;
  /**
   * Kennung eines Hinweises, der das Feld beschreibt (`aria-describedby`).
   * Ist schon etwas gewählt, gibt es kein Eingabefeld — dann trägt der Knopf
   * „Ändern" die Beschreibung, das einzige, was man dort fokussieren kann.
   */
  beschriebenVon?: string;
  /** Steht unter dem Feld, z. B. ein Konflikthinweis (H1.3b). */
  zusatz?: ReactNode;
  /**
   * Höchstens so viele Treffer zeigen, Wortgleiche ausgenommen (`trefferwahl.ts`).
   * Vier ist die Vorgabe: Darüber liest niemand mehr, sondern greift daneben — und
   * beim Anlegen von Orten ist ein Danebengriff eine neue Schreibweise.
   */
  hoechstens?: number;
  /**
   * Ist das gesetzt, darf der Eintrag hier neu angelegt werden. `was` ist das
   * Wort für den Knopf („Ort", „Kunde"); `anlegen` bekommt den getippten Namen
   * und gibt den fertigen Datensatz zurück. Die aufrufende Seite frischt
   * danach ihre Liste auf — sonst steht der neue Eintrag zwar gewählt da,
   * fehlt aber im nächsten Feld.
   */
  neuAnlegen?: {
    was: string;
    /**
     * Was beim Anlegen zusätzlich erfragt wird. Leer heißt: ein Klick genügt.
     *
     * Bei Orten stehen hier Straße, Postleitzahl und Ort — nicht aus
     * Ordnungsliebe, sondern weil ein Ort ohne PLZ und Ort später nicht von
     * seinem Namensvetter zu unterscheiden ist; die Straße kam mit HJ-024
     * dazu, damit sie nicht nach jeder Tour nachgetragen werden muss. Ohne diese Felder tippt man die Anschrift in den
     * Namen; genau das ist im Betrieb passiert.
     */
    zusatzfelder?: { schluessel: string; marke: string; platzhalter?: string }[];
    anlegen: (name: string, zusatz: Record<string, string>) => Promise<Eintrag>;
  };
}) {
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState(false);
  const [legtAn, setLegtAn] = useState(false);
  const [anlegefehler, setAnlegefehler] = useState<string | null>(null);

  /**
   * ═══ DER GERADE ANGELEGTE EINTRAG ═══
   *
   * Nach dem Anlegen steht die neue Kennung sofort im Formular, die neue LISTE
   * aber noch nicht: Das Auffrischen ist eine zweite Abfrage und braucht seine
   * Zeit. In dieser Lücke findet die Anzeige unten die Kennung in `auswahl`
   * nicht — und zeigt wieder das leere Suchfeld. Für die Person sieht das aus,
   * als hätte das Anlegen nicht gezählt, und sie legt den Ort ein zweites Mal
   * an.
   *
   * Deshalb behält dieses Feld den Eintrag selbst, bis die Liste nachgezogen
   * ist. Es ist kein zweiter Speicher: Sobald `auswahl` ihn kennt, gewinnt
   * `auswahl`.
   */
  const [frischAngelegt, setFrischAngelegt] = useState<Eintrag | null>(null);

  /** Offen, solange das kleine Anlegeformular ausgefüllt wird. */
  const [anlegenOffen, setAnlegenOffen] = useState(false);
  const [zusatz, setZusatz] = useState<Record<string, string>>({});

  const gewaehlt = useMemo(() => {
    const ausDerListe =
      eigenschaften.auswahl.find((e) => e.id === eigenschaften.wert) ?? null;
    if (ausDerListe) return ausDerListe;
    // Nur, solange die Liste ihn noch nicht kennt — siehe `frischAngelegt`.
    if (frischAngelegt && frischAngelegt.id === eigenschaften.wert) {
      return frischAngelegt;
    }
    return null;
  }, [eigenschaften.auswahl, eigenschaften.wert, frischAngelegt]);

  const { treffer, weitere } = useMemo(
    () => trefferWaehlen(eigenschaften.auswahl, suche, eigenschaften.hoechstens ?? 4),
    [eigenschaften.auswahl, eigenschaften.hoechstens, suche],
  );

  // Zwei Zeichen sind das Mindeste für einen Namen. Das Anlegen steht trotzdem
  // immer da: Wer es sucht, soll sehen, dass es das gibt und was noch fehlt.
  const nameReicht = suche.trim().length >= 2;
  const darfAnlegen = eigenschaften.neuAnlegen !== undefined && nameReicht;
  // Ohne Eingabe sind die Treffer nur die ersten der Liste, nichts Ähnliches.
  const aehnlicheDa = nameReicht && treffer.length > 0;

  async function neuenAnlegen() {
    if (!eigenschaften.neuAnlegen || !darfAnlegen || legtAn) return;
    setLegtAn(true);
    setAnlegefehler(null);
    try {
      const neu = await eigenschaften.neuAnlegen.anlegen(suche.trim(), zusatz);
      setFrischAngelegt(neu);
      eigenschaften.setzen(neu.id);
      setSuche('');
      setZusatz({});
      setAnlegenOffen(false);
      setOffen(false);
    } catch (problem) {
      setAnlegefehler(
        problem instanceof Error
          ? problem.message
          : 'Das Anlegen hat nicht geklappt.',
      );
    } finally {
      setLegtAn(false);
    }
  }

  if (gewaehlt) {
    return (
      <div className="feld">
        <span className="feld__marke">{eigenschaften.marke}</span>
        <div className="gewaehlt">
          <span>
            {gewaehlt.name}
            {gewaehlt.unter && (
              <span className="wahlzeile__unter">{gewaehlt.unter}</span>
            )}
          </span>
          <button
            type="button"
            className="knopf knopf--still knopf--klein"
            disabled={eigenschaften.disabled}
            aria-describedby={eigenschaften.beschriebenVon}
            onClick={() => {
              eigenschaften.setzen(null);
              setSuche('');
              setOffen(true);
            }}
          >
            Ändern
          </button>
        </div>
        {eigenschaften.zusatz}
      </div>
    );
  }

  return (
    <div className="feld">
      <span className="feld__marke">{eigenschaften.marke}</span>
      <input
        type="search"
        value={suche}
        placeholder={eigenschaften.platzhalter ?? 'Tippen zum Suchen …'}
        disabled={eigenschaften.disabled}
        aria-describedby={eigenschaften.beschriebenVon}
        onFocus={() => setOffen(true)}
        onChange={(e) => {
          setSuche(e.target.value);
          setOffen(true);
        }}
      />
      {eigenschaften.hinweis && (
        <span className="feld__hinweis">{eigenschaften.hinweis}</span>
      )}
      {eigenschaften.zusatz}

      {offen && (
        <div className="stapel" style={{ gap: 4, marginTop: 4 }}>
          {treffer.length === 0 && !eigenschaften.neuAnlegen && (
            <span className="meta">Nichts gefunden.</span>
          )}

          {treffer.map((e) => (
            <button
              type="button"
              key={e.id}
              className="wahlzeile"
              onClick={() => {
                eigenschaften.setzen(e.id);
                // Der getippte Suchtext hat seinen Dienst getan. Bliebe er
                // stehen, tauchte er wieder auf, sobald das Feld von außen
                // geleert wird — nach „Tour eintragen" standen so „Korn" und
                // „Hei" im leeren Formular (HJ-022).
                setSuche('');
                setOffen(false);
              }}
            >
              <span>
                {e.name}
                {e.unter && <span className="wahlzeile__unter">{e.unter}</span>}
              </span>
            </button>
          ))}

          {weitere > 0 && (
            <span className="meta">
              {weitere === 1 ? '1 weiterer Treffer' : `${weitere} weitere Treffer`} – mit
              Straße oder Ort im Suchfeld eingrenzen.
            </span>
          )}

          {eigenschaften.neuAnlegen && (
            <>
              {aehnlicheDa && (
                <span className="meta">
                  Ähnliche Einträge gefunden – prüfe, ob es ihn schon gibt.
                </span>
              )}

              {/* ═══ EIN KLICK ODER EIN KLEINES FORMULAR ═══

                  Ohne Zusatzfelder genügt der Knopf. Bei Orten steht dahinter
                  ein Formular für Straße, Postleitzahl und Ort — weil ein Ort ohne sie
                  später nicht von seinem Namensvetter zu unterscheiden ist.
                  „Korn Recycling" gibt es in Albstadt und in Ulm, und in der
                  Auswahl sähen beide gleich aus.

                  Die Felder sind KEINE Pflicht. Wer die Anschrift nicht zur
                  Hand hat, legt trotzdem an und trägt sie später unter „Orte"
                  nach — ein Formular, das ausbremst, wird umgangen, und der
                  Umweg heißt dann wieder: alles in den Namen tippen. */}
              {anlegenOffen && darfAnlegen && (eigenschaften.neuAnlegen.zusatzfelder?.length ?? 0) > 0 ? (
                <div className="anlegekasten">
                  <div className="anlegekasten__name">„{suche.trim()}"</div>
                  <div className="feldreihe">
                    {eigenschaften.neuAnlegen.zusatzfelder?.map((feld) => (
                      <label className="feld" key={feld.schluessel}>
                        <span className="feld__marke">{feld.marke}</span>
                        <input
                          type="text"
                          value={zusatz[feld.schluessel] ?? ''}
                          placeholder={feld.platzhalter}
                          disabled={legtAn}
                          onChange={(e) =>
                            setZusatz((bisher) => ({
                              ...bisher,
                              [feld.schluessel]: e.target.value,
                            }))
                          }
                        />
                      </label>
                    ))}
                  </div>
                  <div className="reihe reihe--rechts" style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="knopf knopf--still knopf--klein"
                      disabled={legtAn}
                      onClick={() => {
                        setAnlegenOffen(false);
                        setZusatz({});
                      }}
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      className="knopf knopf--haupt knopf--klein"
                      disabled={legtAn}
                      onClick={() => void neuenAnlegen()}
                    >
                      {legtAn
                        ? 'Wird angelegt …'
                        : aehnlicheDa
                          ? 'Ist neu – anlegen'
                          : 'Anlegen'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                <button
                  type="button"
                  className="wahlzeile wahlzeile--neu"
                  disabled={eigenschaften.disabled || legtAn || !darfAnlegen}
                  onClick={() => {
                    if ((eigenschaften.neuAnlegen?.zusatzfelder?.length ?? 0) > 0) {
                      setAnlegenOffen(true);
                      return;
                    }
                    void neuenAnlegen();
                  }}
                >
                  {legtAn
                    ? 'Wird angelegt …'
                    : darfAnlegen
                      ? `„${suche.trim()}" als neuen ${eigenschaften.neuAnlegen.was} anlegen`
                      : `Als neuen ${eigenschaften.neuAnlegen.was} anlegen`}
                </button>
                {!darfAnlegen && (
                  <span className="meta">Dafür erst oben den Namen eintippen.</span>
                )}
                </>
              )}
            </>
          )}

          {anlegefehler && <span className="meta">{anlegefehler}</span>}
        </div>
      )}
    </div>
  );
}
